import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Search } from "lucide-react";
import WishlistButton from "../components/WishlistButton";
import VehicleStatus, { vehicleStatus } from "../components/VehicleStatus";

const fmt = (n) => `$${Number(n).toLocaleString()}`;

export default function Cars() {
    const [params] = useSearchParams();
    const [vehicles, setVehicles] = useState([]);
    const [state, setState] = useState("loading"); // loading | ready | error
    const [q, setQ] = useState(params.get("q") || "");
    const [make, setMake] = useState("all");
    const [maxPrice, setMaxPrice] = useState("");

    const load = () => {
        setState("loading");
        api.get("/vehicles").then(r => { setVehicles(r.data); setState("ready"); }).catch(() => setState("error"));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

    const makes = useMemo(() => ["all", ...Array.from(new Set(vehicles.map(v => v.make)))], [vehicles]);
    const filtered = vehicles
        .filter(v => {
            const matchQ = !q || [v.make, v.model, v.description].join(" ").toLowerCase().includes(q.toLowerCase());
            const matchMake = make === "all" || v.make === make;
            const matchPrice = !maxPrice || v.price <= Number(maxPrice);
            return matchQ && matchMake && matchPrice;
        })
        .sort((a, b) => vehicleStatus(a.status).rank - vehicleStatus(b.status).rank); // available first, sold last
    const availableCount = filtered.filter(v => (v.status || "available") === "available").length;
    const clearFilters = () => { setQ(""); setMake("all"); setMaxPrice(""); };

    return (
        <div data-testid="cars-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
            <div className="mb-12 max-w-3xl">
                <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">Pre-Owned Showroom</div>
                <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Available Vehicles</h1>
                <p className="text-[#33302C] mt-4 max-w-xl font-light">Every vehicle in our showroom is hand-selected, mechanically vetted, and prepared to a meticulous standard.</p>
            </div>

            <div className="grid lg:grid-cols-[260px_1fr] gap-10">
                <aside className="space-y-8" aria-label="Filter vehicles">
                    <div>
                        <label htmlFor="cars-search" className="block text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-3">Search</label>
                        <div className="relative">
                            <Search size={14} aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B645C]" />
                            <Input id="cars-search" data-testid="cars-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Make, model, keyword" className="pl-9 bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                        </div>
                    </div>
                    <div role="group" aria-labelledby="make-filter-label">
                        <div id="make-filter-label" className="text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-3">Make</div>
                        <div className="space-y-1">
                            {makes.map(m => (
                                <button key={m} data-testid={`filter-make-${m}`} aria-pressed={make === m} onClick={() => setMake(m)} className={`block w-full text-left px-3 py-2 text-sm transition-colors ${make === m ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-[#33302C] hover:text-[#1A1A1A]"}`}>
                                    {m === "all" ? "All Makes" : m}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label htmlFor="cars-max-price" className="block text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-3">Max Price</label>
                        <Input id="cars-max-price" data-testid="filter-max-price" type="number" min="0" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="Any" className="bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                    </div>
                </aside>

                <div>
                    {state === "loading" && (
                        <div role="status" aria-label="Loading vehicles" className="grid md:grid-cols-2 gap-6">
                            {[0, 1, 2, 3].map(k => (
                                <div key={k} className="bg-charcoal-800 border border-[#E2D4C1] animate-pulse">
                                    <div className="aspect-[4/3] bg-[#E2D4C1]" />
                                    <div className="p-6 space-y-3"><div className="h-3 w-1/4 bg-[#E2D4C1]" /><div className="h-6 w-1/2 bg-[#E2D4C1]" /></div>
                                </div>
                            ))}
                        </div>
                    )}
                    {state === "error" && (
                        <div data-testid="cars-error" role="alert" className="border border-[#E2D4C1] bg-[#F7F3EB] py-16 px-6 text-center">
                            <p className="text-[#33302C] mb-6">We couldn't load the showroom just now.</p>
                            <button onClick={load} className="px-6 py-3 bg-gold text-white uppercase tracking-[0.2em] text-xs font-semibold hover:bg-gold-hover">Try Again</button>
                        </div>
                    )}
                    {state === "ready" && (
                        <>
                            <div className="text-sm text-[#6B645C] mb-6" data-testid="cars-result-count" aria-live="polite">
                                {availableCount} available · {filtered.length} shown
                            </div>
                            {filtered.length === 0 ? (
                                <div data-testid="cars-empty" className="border border-[#E2D4C1] bg-[#F7F3EB] py-16 px-6 text-center">
                                    <p className="text-[#33302C] mb-2">No vehicles match these filters.</p>
                                    <p className="text-sm text-[#6B645C] mb-6">Tell us what you're looking for and we'll help source it, or save a search in your account to be emailed when a match arrives.</p>
                                    <div className="flex flex-wrap justify-center gap-3">
                                        <button onClick={clearFilters} className="px-6 py-3 border border-[#8C5A3C]/40 uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold">Clear Filters</button>
                                        <Link to="/contact?topic=vehicle" className="px-6 py-3 bg-gold text-white uppercase tracking-[0.2em] text-xs font-semibold hover:bg-gold-hover">Request a Vehicle</Link>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 gap-6">
                                    {filtered.map((v, i) => (
                                        <Link key={v.id} to={`/cars/${v.id}`} data-testid={`car-card-${i}`} className="group block bg-charcoal-800 border border-[#E2D4C1] hover:border-gold/40 transition-all">
                                            <div className="aspect-[4/3] overflow-hidden bg-[#E2D4C1] relative">
                                                {v.image && <img src={v.image} alt={`${v.year} ${v.make} ${v.model}`} loading={i < 2 ? "eager" : "lazy"} decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                                                <div className="absolute top-3 left-3"><VehicleStatus status={v.status} testId={`car-status-${i}`} /></div>
                                                <div className="absolute top-3 right-3"><WishlistButton vehicleId={v.id} testId={`wish-car-${i}`} /></div>
                                            </div>
                                            <div className="p-6">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-1">{v.year}</div>
                                                        <div className="font-display text-2xl">{v.make}</div>
                                                        <div className="text-[#33302C] text-sm">{v.model}</div>
                                                    </div>
                                                    <div className="text-gold font-medium">{fmt(v.price)}</div>
                                                </div>
                                                <div className="text-xs text-[#6B645C] flex gap-4 pt-3 border-t border-[#E2D4C1]">
                                                    <span>{Number(v.mileage).toLocaleString()} mi</span>
                                                    {v.transmission && <span>{v.transmission}</span>}
                                                    {v.fuel && <span>{v.fuel}</span>}
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
